/* eslint-disable better-mutation/no-mutation */

import draftActions from './draft-actions';
import { logger } from 'packages/logger';
import { getSyiSlice } from 'tradera/apps/syi/state/selectors/syi';
import { openAddPhoto } from 'tradera/apps/syi/services/native-app-service';
import { throttledFetchImageAttributeSuggestions } from './attributes-actions';
import { resetImageAttributeSuggestions } from '../reducers/attributes';
import { disableAutoSave, enableAutoSave } from '../reducers/syi';
import * as actions from '../reducers/images';
import type { Image as ImageType, Rotation } from '../reducers/images';
import type { AppDispatch, GetState } from 'tradera/state/configure-store';
import type { SocketIoChannel } from 'tradera/services/socket-io-channel';
import type { ItemDraftImage } from 'tradera/state/services/types/webapi-selling-generated';
import config from 'tradera/apps/syi/services/config';
import scribe from 'tradera/apps/syi/services/scribe';
import axios, { type AxiosError } from 'axios';
import { selectIsFeatureEnabled } from 'tradera/state/environment/selectors';
import { selectImageIds } from 'tradera/apps/syi/state/selectors/image-selectors';
import { fetchClassificationFromFirstImage } from 'tradera/apps/syi/state/actions/predictions-actions';
import { selectDraftId } from 'tradera/apps/syi/state/selectors/draft-selectors';

const MAX_IMAGES_FOR_IMAGE_TO_COLORS = 2;

export const setDraftImages =
    (images: ItemDraftImage[]) => (dispatch: AppDispatch) => {
        dispatch(actions.setDraftImages(images));

        dispatch(draftActions.updateDraftCanBeResumed());

        const removeNullAndUndefinedFilter = <T>(
            n: T | null | undefined
        ): n is T => !!n;
        const draftImageIds = images
            .slice(0, MAX_IMAGES_FOR_IMAGE_TO_COLORS)
            .map((image) => image.draftImage?.imageId)
            .filter(removeNullAndUndefinedFilter);
        if (draftImageIds.length > 0) {
            dispatch(throttledFetchImageAttributeSuggestions(draftImageIds));
        }
    };

const addImageAndUpdateSuggestions =
    (imageId: string, imageUrl: string, classify: boolean) =>
    (dispatch: AppDispatch, getState: GetState) => {
        dispatch(actions.addImage({ imageId, imageUrl }));

        const count = getSyiSlice(getState()).images.images.length;
        if (count <= MAX_IMAGES_FOR_IMAGE_TO_COLORS) {
            const images = getSyiSlice(getState()).images.images;
            handleAttributeSuggestionsForImages(dispatch, images);
        }
        if (classify) {
            const draftId = selectDraftId(getState());
            dispatch(fetchClassificationFromFirstImage({ imageId, draftId }));
        }
        dispatch(draftActions.updateDraftCanBeResumed());
    };

const replaceImageAndUpdateSuggestions =
    (index: number, imageId: string, imageUrl: string) =>
    (dispatch: AppDispatch, getState: GetState) => {
        dispatch(actions.replaceImage({ index, imageUrl, imageId }));
        if (index < MAX_IMAGES_FOR_IMAGE_TO_COLORS) {
            const images = getSyiSlice(getState()).images.images;
            handleAttributeSuggestionsForImages(dispatch, images);
        }
        dispatch(draftActions.updateDraftCanBeResumed());
    };

export const removeImageAndUpdateSuggestions =
    (index: number) => (dispatch: AppDispatch, getState: GetState) => {
        dispatch(actions.removeImage({ index }));

        if (index < MAX_IMAGES_FOR_IMAGE_TO_COLORS) {
            const images = getSyiSlice(getState()).images.images;
            handleAttributeSuggestionsForImages(dispatch, images);
        }
        dispatch(draftActions.updateDraftCanBeResumed());
    };

export const changeImagePosition =
    (from: number, to: number) =>
    (dispatch: AppDispatch, getState: GetState) => {
        dispatch(actions.changeImagePosition({ from, to }));
        const isImageToColorImageChanged =
            (from >= MAX_IMAGES_FOR_IMAGE_TO_COLORS &&
                to < MAX_IMAGES_FOR_IMAGE_TO_COLORS) ||
            (to >= MAX_IMAGES_FOR_IMAGE_TO_COLORS &&
                from < MAX_IMAGES_FOR_IMAGE_TO_COLORS);
        if (isImageToColorImageChanged) {
            const images = getSyiSlice(getState()).images.images;
            handleAttributeSuggestionsForImages(dispatch, images);
        }
    };

export const removeImageWithId =
    (imageId: unknown) => (dispatch: AppDispatch, getState: GetState) => {
        const images = getSyiSlice(getState()).images.images;
        let imageIndex = -1;
        for (let i = images.length - 1; i >= 0; i--) {
            if (images[i].imageId === imageId) {
                imageIndex = i;
                break;
            }
        }
        if (imageIndex > -1) {
            dispatch(removeImageAndUpdateSuggestions(imageIndex));
        }

        dispatch(draftActions.updateDraftCanBeResumed());
    };

export const rotateImageClockwise =
    (index: number) => (dispatch: AppDispatch, getState: GetState) => {
        const images = getSyiSlice(getState()).images.images;
        let rotationDegrees = images[index]?.rotationDegrees || (0 as Rotation);
        switch (rotationDegrees) {
            case 0:
                rotationDegrees = 90;
                break;
            case 90:
                rotationDegrees = 180;
                break;
            case 180:
                rotationDegrees = 270;
                break;
            case 270:
                rotationDegrees = 0;
                break;
        }

        dispatch(
            actions.setImageRotation({
                index,
                rotationDegrees
            })
        );
    };

const allowedFileExtensions = ['.jpeg', '.jpg', '.png', '.gif'];
const maxFileSize = 1024 * 1024 * 20;
const validateFile = (file: File) => {
    if (file === undefined) {
        return 'syi_validation_images_upload_error'; //'Något blev fel vid uppladdningen av filen';
    }
    if (
        !allowedFileExtensions.some((fileExt) =>
            file.name.toLowerCase().endsWith(fileExt)
        )
    ) {
        return 'syi_validation_images_upload_wrong_type'; //'Filen du laddade upp är av fel typ.';
    }
    if (file.size > maxFileSize) {
        return 'syi_validation_images_upload_too_large'; //'Filen du laddade upp är för stor.';
    }
    return false;
};

export const imagesDroppedThunk =
    (files: FileList, isTemplate: boolean) => async (dispatch: AppDispatch) => {
        await dispatch(uploadImagesThunk(files, isTemplate));
    };

export const imagesSelectedThunk =
    (fileInput: HTMLInputElement, isTemplate: boolean) =>
    async (dispatch: AppDispatch) => {
        if (!fileInput.files) return;
        await dispatch(uploadImagesThunk(fileInput.files, isTemplate));
        fileInput.value = '';
    };

export const imageCroppedThunk =
    (file: File, isTemplate: boolean, index: number) =>
    async (dispatch: AppDispatch) => {
        if (file.type && file.type.startsWith('image/')) {
            const validationResult = validateFile(file);
            if (validationResult) {
                dispatch(
                    actions.setImageUploadFailed({
                        errorMessage: validationResult
                    })
                );
                return;
            }
            await dispatch(uploadImageWithFormData(file, isTemplate, index));
        }
    };

const uploadImagesThunk =
    (files: FileList, isTemplate: boolean) =>
    async (dispatch: AppDispatch, getState: GetState) => {
        const imagesLeft =
            actions.MAX_NUMBER_OF_IMAGES -
            getSyiSlice(getState()).images.images.length;
        const imagesToUpload = Array.from(files).slice(0, imagesLeft);

        dispatch(
            actions.setImagesUploading({
                numberOfImages: imagesToUpload.length
            })
        );

        for (const file of imagesToUpload) {
            const validationResult = validateFile(file);
            if (validationResult) {
                dispatch(
                    actions.setImageUploadFailed({
                        errorMessage: validationResult
                    })
                );
                return;
            }
        }
        for (const file of imagesToUpload) {
            await dispatch(uploadImageWithFormData(file, isTemplate));
        }
    };

const uploadImageWithFormData =
    (file: File, isTemplate: boolean, index?: number) =>
    async (dispatch: AppDispatch, getState: GetState) => {
        const startTime = new Date().getTime();
        const formData = new FormData();
        formData.append('imageFile', file);

        const url = isTemplate
            ? config.templateImagesUrl
            : config.draftImagesUrl;
        try {
            const response = await axios.post(url, formData);
            const { errorCode, imageId, imageUrl } = response.data;
            if (errorCode) {
                if (errorCode === 'ImageMinimumDimensionsNotReached') {
                    dispatch(
                        actions.setImageUploadFailed({
                            errorMessage:
                                'syi_validation_images_upload_too_small'
                        })
                    );
                }

                return;
            }

            // onImageUploaded start //
            const downloadingImage = new Image();
            downloadingImage.onload = () => {
                if (typeof index === 'number') {
                    dispatch(
                        replaceImageAndUpdateSuggestions(
                            index,
                            imageId,
                            imageUrl
                        )
                    );
                } else {
                    const isMagicalSyiActive =
                        selectIsFeatureEnabled('magical-syi')(getState());

                    const isFirstImage =
                        getSyiSlice(getState()).images.images.length === 0;

                    dispatch(
                        addImageAndUpdateSuggestions(
                            imageId,
                            imageUrl,
                            isFirstImage && isMagicalSyiActive
                        )
                    );
                }
            };
            downloadingImage.src = imageUrl;
            // onImageUploaded end //

            const endTime = new Date().getTime();
            scribe.sendDuration(
                'image_upload_client_time',
                endTime - startTime
            );
        } catch (error: unknown) {
            dispatch(
                actions.setImageUploadFailed({
                    errorMessage: 'syi_validation_images_upload_error' //'Ett fel inträffade vid uppladdningen'
                })
            );
            if (
                !(error as AxiosError).response &&
                (error as AxiosError).request
            ) {
                // The request was made but no response was received
                logger('Image upload failed. Reason: No response');
            } else {
                // Something happened in setting up the request that triggered an Error
                logger(
                    'Image upload failed. Reason: ' + (error as Error).message
                );
            }
        }
    };

class MissingDraftId extends Error {}

const hasRequest = (value: unknown) =>
    typeof value === 'object' && value && 'request' in value && !!value.request;

const hasResponse = (value: unknown) =>
    typeof value === 'object' &&
    value &&
    'response' in value &&
    !!value.response;

export const nativeAppOpenAddPhotoThunk =
    (
        saveNowIfQueued: (
            draftId?: string
        ) => Promise<{ draftId?: string } | undefined>
    ) =>
    async (dispatch: AppDispatch, getState: GetState) => {
        dispatch(setIsUploadingWithApp(true));
        try {
            dispatch(disableAutoSave());
            const currentDraftId = getSyiSlice(getState())?.draft?.draftId;
            const draft = await saveNowIfQueued(currentDraftId);
            const draftId = draft?.draftId || currentDraftId;
            if (!draftId) {
                throw new MissingDraftId();
            }
            await openAddPhoto(draftId);
        } catch (error) {
            if (error instanceof MissingDraftId) {
                logger('Missing draftId on app photo upload');
            } else if (!hasRequest(error) || hasResponse(error)) {
                // Do not log axios request errors, as they are client dependent (such as offline)
                logger(error);
            }
            dispatch(
                actions.setImageUploadFailed({
                    errorMessage: 'syi_images_photo_with_mobile_error'
                })
            );
            dispatch(setIsUploadingWithApp(false));
            dispatch(enableAutoSave());
        }
    };

export const fetchImagesFromDraft =
    (draftId: string, draftSocket: SocketIoChannel) =>
    async (dispatch: AppDispatch, getState: GetState) => {
        if (!draftId) {
            logger('DraftChanged event missing draft ID');
        }
        const imagesHaveChanged = await dispatch(
            draftActions.loadImagesFromDraft(draftId)
        );
        if (imagesHaveChanged) {
            dispatch(cancelMobileUpload(draftSocket));

            const isMagicalSyiActive =
                selectIsFeatureEnabled('magical-syi')(getState());

            const imageId = selectImageIds(getState())[0];

            const shouldClassify = isMagicalSyiActive && imageId;

            if (shouldClassify) {
                dispatch(
                    fetchClassificationFromFirstImage({
                        imageId: imageId.toString(),
                        draftId
                    })
                );
            }
        }
    };

export const setIsUploadingWithApp =
    (isUploadingWithApp: boolean) => (dispatch: AppDispatch) => {
        dispatch(
            actions.setMobileUpload({
                isUploadingWithApp
            })
        );
    };

export const cancelMobileUpload =
    (draftSocket: SocketIoChannel) => (dispatch: AppDispatch) => {
        draftSocket.disconnect();
        dispatch(setIsUploadingWithApp(false));
        dispatch(enableAutoSave());
    };

const handleAttributeSuggestionsForImages = (
    dispatch: AppDispatch,
    images: ImageType[]
) => {
    const imageIds: unknown[] = [];
    images.slice(0, MAX_IMAGES_FOR_IMAGE_TO_COLORS).forEach((image) => {
        if (image.imageId && !image.isListingImage) {
            imageIds.push(image.imageId);
        }
    });
    if (imageIds.length > 0) {
        dispatch(throttledFetchImageAttributeSuggestions(imageIds));
    } else {
        dispatch(resetImageAttributeSuggestions());
    }
};

export const fetchImageAttributeSuggestions =
    () => (dispatch: AppDispatch, getState: GetState) => {
        const images = getSyiSlice(getState()).images.images;
        handleAttributeSuggestionsForImages(dispatch, images);
    };

export const reset = () => (dispatch: AppDispatch) => {
    dispatch(setDraftImages([]));
    dispatch(draftActions.updateDraftCanBeResumed());
};
